import * as THREE from 'three';
import { JOINT_CONSTRAINTS, clamp, getJointRotationOrder } from './constraints';

export function clampJoint(jointName: string, angles: [number, number, number]): [number, number, number] {
  const limits = JOINT_CONSTRAINTS[jointName];
  if (!limits) return angles;
  return [
    clamp(angles[0], limits.x[0], limits.x[1]),
    clamp(angles[1], limits.y[0], limits.y[1]),
    clamp(angles[2], limits.z[0], limits.z[1])
  ];
}

/**
 * Solves a 2-bone IK chain.
 * @param rootName The name of the shoulder/hip joint
 * @param hingeName The name of the elbow/knee joint
 * @param targetPos Local position of the target relative to the root joint
 * @param L1 Length of bone 1 (upper arm/leg)
 * @param L2 Length of bone 2 (lower arm/leg)
 * @param poleVector Local direction for the elbow/knee to point towards
 */
export function solveLimbIK(
  rootName: string,
  hingeName: string,
  targetPos: { x: number, y: number, z: number },
  L1: number,
  L2: number,
  poleVector?: { x: number, y: number, z: number }
): { rootAngles: [number, number, number], hingeAngles: [number, number, number] } {
  const dir = new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z);
  const distance = clamp(dir.length(), 0.01, L1 + L2 - 0.001);
  dir.normalize();

  // 1. Calculate hinge angle (Law of Cosines)
  let cosAngle = (L1 * L1 + L2 * L2 - distance * distance) / (2 * L1 * L2);
  cosAngle = clamp(cosAngle, -1, 1);
  const innerAngle = Math.acos(cosAngle);
  const bendAngleRad = Math.PI - innerAngle;
  let bendAngleDeg = bendAngleRad * (180 / Math.PI);

  let hingeAngles: [number, number, number] = [0, 0, 0];
  const isElbow = hingeName.toLowerCase().includes('elbow');
  if (isElbow) {
    hingeAngles[0] = -bendAngleDeg; // Elbows bend negative X
  } else {
    hingeAngles[0] = bendAngleDeg; // Knees bend positive X
  }

  // 2. Calculate root pointing rotation
  const defaultDir = new THREE.Vector3(0, -1, 0); // Limbs point down by default
  const qPoint = new THREE.Quaternion().setFromUnitVectors(defaultDir, dir);

  // 3. Offset root by the triangle alpha angle so the wrist hits the target
  const alpha = Math.asin(clamp((L2 * Math.sin(innerAngle)) / distance, -1, 1));
  
  // Pitch offset depends on joint orientation
  const pitchOffsetRad = isElbow ? alpha : -alpha;
  const qOffset = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitchOffsetRad);
  
  const qFinal = qPoint.clone().multiply(qOffset);

  // 4. Handle pole vector (twist) if provided
  if (poleVector && (poleVector.x !== 0 || poleVector.y !== 0 || poleVector.z !== 0)) {
    const pole = new THREE.Vector3(poleVector.x, poleVector.y, poleVector.z).normalize();
    // Calculate current elbow direction (which lies in the XY plane before full rotation)
    const currentElbowDir = new THREE.Vector3(0, 0, isElbow ? 1 : -1).applyQuaternion(qFinal).normalize();
    
    // Project pole and current elbow dir onto the plane normal to 'dir'
    const planeNormal = dir.clone();
    const projPole = pole.clone().projectOnPlane(planeNormal).normalize();
    const projCurrent = currentElbowDir.clone().projectOnPlane(planeNormal).normalize();
    
    if (projPole.lengthSq() > 0.01 && projCurrent.lengthSq() > 0.01) {
      const twistAngle = projCurrent.angleTo(projPole);
      const cross = new THREE.Vector3().crossVectors(projCurrent, projPole);
      const sign = Math.sign(cross.dot(planeNormal));
      
      const qTwist = new THREE.Quaternion().setFromAxisAngle(planeNormal, twistAngle * sign);
      qFinal.premultiply(qTwist);
    }
  }

  // 5. Convert back to Euler using the specific joint's rotation order
  const order = getJointRotationOrder(rootName) as THREE.EulerOrder;
  const finalEuler = new THREE.Euler().setFromQuaternion(qFinal, order);

  let rootAngles: [number, number, number] = [
    finalEuler.x * (180 / Math.PI),
    finalEuler.y * (180 / Math.PI),
    finalEuler.z * (180 / Math.PI)
  ];

  return {
    rootAngles: clampJoint(rootName, rootAngles),
    hingeAngles: clampJoint(hingeName, hingeAngles)
  };
}
